import { Package, ShieldCheck, ArrowRight } from 'lucide-react'
import { Image } from '@/shared/components/ui/image'
import type { HoldingSummary } from '@/consumer/hooks/use-holdings'

export function WarrantyBadge({ status }: { status: string }) {
  const isActive = status === 'ACTIVE'
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border-[0.5px] px-2.5 py-1 text-xs uppercase tracking-[0.08em] ${
        isActive
          ? 'border-[var(--green-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
          : 'border-[var(--status-error-border,rgba(153,27,27,0.2))] bg-[var(--status-error-bg)] text-[var(--status-error)]'
      }`}
    >
      <ShieldCheck className="h-3 w-3" />
      {isActive ? 'Active' : 'Expired'}
    </span>
  )
}

export function ProductCard({ holding, onClick }: { holding: HoldingSummary; onClick: () => void }) {
  const hasHost = holding.host && (holding.host.registration_number || holding.host.make)

  return (
    <div
      className="group flex gap-5 p-5 rounded-[8px] transition-all duration-150 cursor-pointer bg-[var(--bg-card)] border-[0.5px] border-solid border-[var(--rule)] hover:border-[var(--green-border)]"
      onClick={onClick}
    >
      {/* Product image */}
      <Image
        src={holding.product.image_url}
        alt={holding.product.name}
        className="h-20 w-20 flex-shrink-0 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]"
        fallback={<Package className="h-8 w-8 text-[var(--ink-3)]" />}
      />
      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="mb-1 truncate text-base leading-snug text-[var(--ink)]">
          {holding.product.name}
        </p>
        <p className="mb-2 truncate text-sm text-[var(--ink-3)]">
          {holding.product.template_name || holding.organisation.name}
          {hasHost && (
            <>
              {holding.host!.registration_number && (
                <> &middot; <span className="font-medium tracking-wide">{holding.host!.registration_number}</span></>
              )}
              {holding.host!.make && (
                <> &middot; {holding.host!.make}</>
              )}
            </>
          )}
        </p>

        <div className="flex items-center justify-between">
          {holding.warranty ? (
            <WarrantyBadge status={holding.warranty.status} />
          ) : (
            <span className="text-xs uppercase tracking-[0.08em] text-[var(--ink-3)]">
              No warranty
            </span>
          )}
          <div className="flex items-center gap-1.5 text-xs text-[var(--ink-3)]">
            {holding.organisation.logo_url ? (
              <img
                src={holding.organisation.logo_url}
                alt={holding.organisation.name}
                className="h-4 max-w-[80px] object-contain opacity-60"
              />
            ) : (
              <span>{holding.organisation.name}</span>
            )}
            <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      </div>
    </div>
  );
}
